import type { BaseEntity } from './common';
import type { BillingPeriod } from './plan';

/** Dia da semana no contrato do backend (`java.time.DayOfWeek`). */
export type WeekdayCode =
  | 'MONDAY'
  | 'TUESDAY'
  | 'WEDNESDAY'
  | 'THURSDAY'
  | 'FRIDAY'
  | 'SATURDAY'
  | 'SUNDAY';

/**
 * Ordem (segunda → domingo) e rótulo dos dias, para os seletores do backoffice.
 * A ordem importa: é a mesma em que o backend serializa e monta o rótulo.
 */
export const WEEKDAYS: ReadonlyArray<{ code: WeekdayCode; label: string }> = [
  { code: 'MONDAY', label: 'Segunda' },
  { code: 'TUESDAY', label: 'Terça' },
  { code: 'WEDNESDAY', label: 'Quarta' },
  { code: 'THURSDAY', label: 'Quinta' },
  { code: 'FRIDAY', label: 'Sexta' },
  { code: 'SATURDAY', label: 'Sábado' },
  { code: 'SUNDAY', label: 'Domingo' },
];

/**
 * Kardume Pass plan (planos do consumidor): tier escolhido no cadastro ou
 * assinado depois, com limite de resgate de cupom/QR Code. Não confundir com
 * `PlanDto`, que é o plano comercial do lojista.
 */
export interface PassPlanDto extends BaseEntity {
  code: string;
  name: string;
  description?: string;
  active: boolean;
  billingPeriod: BillingPeriod;
  price: number;
  isDefault: boolean;
  publiclyVisible: boolean;
  highlighted: boolean;
  displayOrder: number;
  monthlyRedemptionLimit?: number;
  unlimitedRedemptions: boolean;
  cashbackBonusPercent: number;
  adFree: boolean;
  earlyAccessPromotions: boolean;
  prioritySupport: boolean;
  features: string[];

  /**
   * Dias da semana em que o plano permite resgatar cupom/QR Code
   * (`'MONDAY' | 'TUESDAY' | ...`). Semana completa = sem restrição.
   */
  redemptionDays: WeekdayCode[];

  /**
   * Mesmo conjunto em texto pt-BR ("Segunda a quinta", "Todos os dias"),
   * calculado no backend para backoffice, site e app mostrarem o mesmo rótulo.
   */
  redemptionDaysLabel: string;

  subscribersCount?: number;
}

/**
 * O mínimo de um plano do Pass para um seletor.
 *
 * A rota pública `/v1/pass/plans` devolve o plano do CONSUMIDOR, que não traz
 * `createdAt`/`updatedAt` nem `active` - não é um [PassPlanDto]. Para o painel
 * do lojista escolher "exclusiva Pass a partir do plano X" basta o id, o nome e
 * a ordem, e é só isso que este tipo promete.
 */
export interface PassPlanOptionDto {
  id: string;
  name: string;
  displayOrder: number;
}

export interface PassPlanRequestDto {
  code: string;
  name: string;
  description?: string;
  active: boolean;
  billingPeriod: BillingPeriod;
  price: number;
  isDefault: boolean;
  publiclyVisible: boolean;
  highlighted: boolean;
  displayOrder: number;
  monthlyRedemptionLimit?: number;
  unlimitedRedemptions: boolean;
  cashbackBonusPercent: number;
  adFree: boolean;
  earlyAccessPromotions: boolean;
  prioritySupport: boolean;
  features: string[];
  /** Lista vazia = semana completa (o backend normaliza). */
  redemptionDays: WeekdayCode[];
}
